import React, { useState, useContext } from "react"
import { Link, useHistory } from "react-router-dom"
import { SmartDateFormat, formatPlaylistDuration } from "../../../util/dates"
import { PauseCircle, PlayCircle, PlusCircle } from "react-bootstrap-icons"
import { usePlaylistFollow } from "../../../hooks/useUserPlaylists"
import { Spinner } from "../../shared/Spinner"
import { RequestError } from "../../shared/RequestError"
import CommentSection from "../../comments/CommentSection"
import { useDispatch, useSelector } from "react-redux"
import {
  prependAndPlay,
  pushToQueue,
  playOrPause,
} from "../../../stores/mediaPlayerSlice"
import { openSongActions } from "../../../stores/songActionsSlice"
import { PostListItemExpandedStyles } from "../constants"
import { ShareUrlButton } from "../../ShareUrlButton"
import { ShowHideButton } from "../../shared/Button"
import { ReactionBlock } from "../ReactionBlock"
import RelatedPosts from "../../RelatedPosts"
import PremiereHeader from "../PremiereHeader"
import clsx from "clsx"
import styles from "../styles.module.css"
import { PlaylistListItemParams, PlaylistSong } from "../../../types/playlists"
import { usePlaylistDetails, getPlaylistImage, sortedSongs } from "."
import { UserContext } from "../../HomeApp"
import EditManageHeader from "../../EditManageHeader"
import CreatePlaylistForm from "../../CreatePlaylistForm"
import { removeFromPlaylist, reorderPlaylistPost } from "../../../util/backend_api/playlists"
import { DropResult } from "@hello-pangea/dnd"
import { useQueryClient } from "react-query"
import { toast } from "react-toastify"

const ICON_SIZE = "1.6rem"
const MARGIN_SPACING = "0rem 0.5rem 0rem 0"

const trackListingToast = (playlistTitle: string) =>
  toast(`${playlistTitle} track listing updated`, {
    toastId: "playlist-track-update",
    position: "bottom-right",
    autoClose: 1500,
    hideProgressBar: true,
    closeOnClick: true,
  })

const PlaylistItemExpanded: React.FC<PlaylistListItemParams> = ({
  playlist,
  updateStatus,
  adminMode = false,
  isPostPage = false,
}) => {
  const metaClass = isPostPage ? "col-span-12 pt-2" : PostListItemExpandedStyles.metaDiv
  const [postStatus, setPostStatus] = useState(playlist.status)
  const [showEditForm, setShowEditForm] = useState(false)
  const [deleted, setDeleted] = useState(false)
  const [localCoverImageUrl, setLocalCoverImageUrl] = useState<string | undefined>(undefined)
  const [localSongs, setLocalSongs] = useState<PlaylistSong[] | null>(null)
  const { user } = useContext(UserContext)
  const isOwner = user && user.band && user.band.id === playlist.band_id
  const playlistId = playlist.playlist_id
  const { isFollowing, isPending, toggle: toggleFollow } = usePlaylistFollow(playlistId, user?.id)
  const history = useHistory()
  const queryClient = useQueryClient()
  const { playlistDetailData, playlistDetails } = usePlaylistDetails(playlist.id)

  const dispatch = useDispatch()
  const currentMediaItem = useSelector(
    (state: any) => state.mediaPlayer.value.currentMediaItem,
  )
  const playerState = useSelector(
    (state: any) => state.mediaPlayer.value.playerState,
  )
  const isCurrentlyPlaying =
    playerState === "playing" && currentMediaItem?.playlistId === playlist.id
  const isCurrentlyQueued =
    playerState === "paused" && currentMediaItem?.playlistId === playlist.id

  const displaySongs: PlaylistSong[] = localSongs ?? sortedSongs(playlistDetails, playlist)

  const onPlayClick = (e: React.MouseEvent) => {
    dispatch(prependAndPlay(sortedSongs(playlistDetails, playlist)))
  }

  const onPlayOrPauseClick = (e: React.MouseEvent) => {
    dispatch(playOrPause({}))
  }

  const onAddClick = (e: React.MouseEvent) => {
    dispatch(pushToQueue(sortedSongs(playlistDetails, playlist)))
  }

  const handleRemoveSong = async (song: PlaylistSong) => {
    if (!song.playlist_post_id) return
    const updated = displaySongs.filter((s) => s.playlist_post_id !== song.playlist_post_id)
    setLocalSongs(updated)
    await removeFromPlaylist(song.playlist_post_id)
    queryClient.invalidateQueries(["post", playlist.id])
    trackListingToast(playlist.title)
  }

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return
    const reordered = Array.from(displaySongs)
    const [moved] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, moved)
    setLocalSongs(reordered)
    const newTrackNumber = result.destination.index + 1
    if (moved.playlist_post_id) {
      await reorderPlaylistPost(moved.playlist_post_id, newTrackNumber)
      queryClient.invalidateQueries(["post", playlist.id])
      trackListingToast(playlist.title)
    }
  }

  if (deleted) return null


  if (showEditForm) {
    return (
      <CreatePlaylistForm
        bandId={playlist.band_id}
        existingPlaylist={playlist}
        songs={displaySongs}
        onRemoveSong={handleRemoveSong}
        onDragEnd={handleDragEnd}
        onSuccess={(coverImageUrl) => {
          if (coverImageUrl) setLocalCoverImageUrl(coverImageUrl)
          setShowEditForm(false)
        }}
        onDelete={() => {
          if (isPostPage) {
            history.push("/")
          } else {
            setDeleted(true)
          }
        }}
        onCancel={() => setShowEditForm(false)}
      />
    )
  }

  return (
    <div className="grid grid-cols-12">
      <PremiereHeader post={playlist} size="large" />
      <div className={PostListItemExpandedStyles.contentDiv}>
        <img
          src={localCoverImageUrl || getPlaylistImage(playlist)}
          alt={playlist.title}
          className="w-full"
          style={{ aspectRatio: "1/1" }}
        />
      </div>
      <div className={metaClass}>
        <EditManageHeader
          isOwner={isOwner}
          post={playlist}
          setShowEditForm={setShowEditForm}
        />
        <div className="flex">
          <div className="flex flex-none">
            {isCurrentlyPlaying ? (
              <PauseCircle
                className="cursor-pointer"
                style={{ minWidth: ICON_SIZE, width: ICON_SIZE, height: ICON_SIZE, margin: MARGIN_SPACING }}
                onClick={onPlayOrPauseClick}
              />
            ) : (
              <PlayCircle
                className="cursor-pointer"
                style={{ minWidth: ICON_SIZE, width: ICON_SIZE, height: ICON_SIZE, margin: MARGIN_SPACING }}
                onClick={isCurrentlyQueued ? onPlayOrPauseClick : onPlayClick}
              />
            )}
          </div>
          <div className="flex items-start justify-between flex-grow">
            <h2
              className={clsx(
                PostListItemExpandedStyles.header,
                "inline mr-2",
                playlist.is_bn_premiere && styles.premiereTitle,
              )}
              dangerouslySetInnerHTML={{ __html: playlist.title }}
            />
            <div className="flex flex-col sm:flex-row flex-none">
              {!isOwner && (
                <button
                  type="button"
                  onClick={toggleFollow}
                  disabled={isPending}
                  className="text-sm border border-gray-400 rounded-sm px-2 ml-2 mb-2 text-gray-600 hover:border-gray-600 hover:text-gray-800 focus:outline-none disabled:opacity-50"
                  style={{ height: 30 }}
                >
                  {isFollowing ? "Following" : "Follow"}
                </button>
              )}
              <ShareUrlButton nadaUrl={playlist.nada_url} renderMode="button" />
            </div>
          </div>
        </div>
        <h3>
          <Link
            className={PostListItemExpandedStyles.bandName}
            to={playlist.local_path}
          >
            {playlist.band_name}
          </Link>
        </h3>
        <div className="text-xs">
          <div>
            Created&nbsp;
            {SmartDateFormat({
              dateStr: playlist.published_at,
              includeTime: false,
            })}
          </div>
        </div>
        {playlist.description && (
          <div className={PostListItemExpandedStyles.description}>
            {playlist.description}
          </div>
        )}
        <div className="text-xs text-gray-500 mb-4">
          {displaySongs.length || 0} songs
          {(() => {
            const dur = formatPlaylistDuration(displaySongs)
            return dur ? `, ${dur}` : null
          })()}
        </div>
        <div className="mt-4">
          <RelatedPosts postId={playlist.id} />
        </div>
        <div className="mt-4">
          <ReactionBlock
            contentId={playlist.id}
            contentType="Post"
            postId={playlist.id}
          />
        </div>
        <div className="mt-2">
          {playlistDetailData.status === "loading" ? (
            <Spinner className="text-gray-500" />
          ) : playlistDetailData.status === "error" ? (
            <RequestError error={playlistDetailData.error} />
          ) : (
            <>
              {displaySongs.length > 0 ? (
                <ul className="text-sm text-black">
                  {displaySongs.map((song: PlaylistSong, index: number) => {
                    const isCurrentlyPlayingSong = playerState === "playing" && currentMediaItem?.postId === song.id
                    const isCurrentlyQueuedSong = playerState === "paused" && currentMediaItem?.postId === song.id
                    return (
                      <li key={song.playlist_post_id ?? song.id} className="mb-2">
                        <div className="flex items-center">
                          {isCurrentlyPlayingSong ? (
                            <PauseCircle
                              className="cursor-pointer flex-none"
                              style={{ minWidth: ICON_SIZE, width: ICON_SIZE, height: ICON_SIZE, margin: MARGIN_SPACING }}
                              onClick={() => dispatch(playOrPause({}))}
                            />
                          ) : (
                            <PlayCircle
                              className="cursor-pointer flex-none"
                              style={{ minWidth: ICON_SIZE, width: ICON_SIZE, height: ICON_SIZE, margin: MARGIN_SPACING }}
                              onClick={() =>
                                isCurrentlyQueuedSong
                                  ? dispatch(playOrPause({}))
                                  : dispatch(prependAndPlay([song]))
                              }
                            />
                          )}
                          <PlusCircle
                            className="cursor-pointer flex-none"
                            style={{ minWidth: ICON_SIZE, width: ICON_SIZE, height: ICON_SIZE, margin: MARGIN_SPACING }}
                            onClick={() => dispatch(openSongActions(song))}
                          />
                          <span className="font-bold flex-none">{index + 1}.</span>
                          &nbsp;
                          <span className={clsx("truncate", song.streaming_url ? "text-gray-700" : "text-gray-400")}>
                            {song.title}
                          </span>
                          <Link
                            to={song.local_path}
                            className="italic pl-1 text-gray-500 text-xs self-center flex-none"
                          >
                            {song.band_name}
                          </Link>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <div className="text-sm text-gray-500">
                  No songs in this playlist
                </div>
              )}
            </>
          )}
        </div>
        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}
        <div className="mt-6">
          <CommentSection
            postId={playlist.id}
            headerText="Comments:"
            className="mb-4"
          />
        </div>
      </div>
    </div>
  )
}

export default PlaylistItemExpanded
